import "./Select.css";

import RcSelect, { Option } from "rc-select";
import { ReactNode, useEffect, useMemo, useState } from "react";

import { DropdownLightIcon } from "../../../assets";
import { SelectData } from "../../../models";
import { FormLabel } from "../FormLabel";
import { getDropdownPopupContainer } from "../utils/getDropdownPopupContainer";

export interface SelectProps {
  label?: string;
  allowLabelDangerousHtml?: boolean;
  initialKey?: string | null;
  value?: string | null;
  placeholder?: string;
  className?: string;
  dropdownMatchSelectWidth?: boolean;
  data: SelectData;
  order?: string[];
  onChange: (value: string) => void;
  labelAlignment?: "left" | "right" | "top" | "bottom";
  disabled?: boolean;
  tooltip?: ReactNode;
  /** "left" puts the tooltip icon straight after the label text; "right" pushes it to the far end of the row. */
  tooltipJustify?: "left" | "right";
  hasBorder?: boolean;
  fontSize?: "body1" | "body2" | "body3";
  corners?: "default" | "rounded";
  size?: "small" | "medium";
  isForTableRepeater?: boolean;
  hasError?: boolean;
  /** Enables a type-to-filter search box in the dropdown. */
  showSearch?: boolean;
  /** Extra class on the (portaled) dropdown popup, for scoping styles to it. */
  dropdownClassName?: string;
}

export const Select = ({
  label,
  allowLabelDangerousHtml,
  initialKey,
  value,
  placeholder = "Please select",
  className,
  dropdownMatchSelectWidth,
  data,
  order,
  onChange,
  labelAlignment = "top",
  disabled,
  tooltip,
  tooltipJustify = "right",
  hasBorder = true,
  fontSize = "body1",
  corners = "default",
  size = "medium",
  isForTableRepeater = false,
  hasError = false,
  showSearch = false,
  dropdownClassName,
}: SelectProps): JSX.Element => {
  const [selectedValue, setSelectedValue] = useState(value);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const onChangeInternal = (val: string): void => {
    setSelectedValue(val);
    onChange(val);
  };

  const orderedData = useMemo<SelectData>(() => {
    if (order) {
      const updatedData: SelectData = [];

      order?.forEach((ord) => {
        const option = data.find((d) => d.key === ord);

        if (option) {
          updatedData.push(option);
        }
      });

      return updatedData;
    }

    return data;
  }, [order, data]);

  const options = orderedData?.map((kvp) => (
    <Option key={kvp.key} value={kvp.key}>
      {kvp.value}
    </Option>
  ));

  return (
    <FormLabel
      label={label}
      tooltip={tooltip}
      tooltipJustify={tooltipJustify}
      labelAlignment={labelAlignment}
      allowDangerousHtml={allowLabelDangerousHtml}
    >
      <div
        className={`${className || ""} form_field ${hasBorder ? "" : "SelectNoBorder"} ${fontSize} ${corners} ${size} ${isForTableRepeater ? "SelectForTableRepeater" : ""} ${disabled ? "SelectDisabled" : ""} ${hasError ? "SelectHasError" : ""}`}
      >
        <RcSelect
          defaultValue={initialKey}
          value={selectedValue}
          suffixIcon={<DropdownLightIcon />}
          menuItemSelectedIcon={undefined}
          placeholder={placeholder || undefined}
          onChange={onChangeInternal}
          showSearch={showSearch}
          optionFilterProp={showSearch ? "children" : undefined}
          prefixCls="Select"
          dropdownClassName={dropdownClassName}
          dropdownMatchSelectWidth={dropdownMatchSelectWidth}
          getPopupContainer={getDropdownPopupContainer}
          dropdownAlign={{ overflow: { adjustY: 0 } }}
          disabled={disabled}
        >
          {options}
        </RcSelect>
      </div>
    </FormLabel>
  );
};
